import { type FlightSummary, formatFlightDesignator } from "@seagames/contracts";
import { wallTimeIn } from "../format/datetime";

/**
 * Presentation helpers for the A&D screens (and the day pickers elsewhere).
 * Pure functions; no rule lives here that the API does not already enforce.
 */

// ── Dates (`YYYY-MM-DD`, calendar days in the games' zone) ─────────────────

const DAY_MS = 86_400_000;
const toUtc = (ymd: string) => Date.parse(`${ymd}T00:00:00Z`);

export const isYmd = (v: string | undefined): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(toUtc(v));

/** `2026-10-13` + 2 → `2026-10-15`. */
export function addDays(ymd: string, n: number): string {
	return new Date(toUtc(ymd) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export const daysBetween = (a: string, b: string): number => Math.round((toUtc(b) - toUtc(a)) / DAY_MS);

/** Today in `timeZone` (the games'), as `YYYY-MM-DD`. */
export function todayIn(timeZone: string, now = new Date()): string {
	return wallTimeIn(now.toISOString(), timeZone)?.date ?? now.toISOString().slice(0, 10);
}

// ── Status ──────────────────────────────────────────────────────────────────

/** Only some of the group runs late ("15 of 16 delayed"). */
export const partlyDelayed = (g: { delayed_count: number; people: number }): boolean => g.delayed_count > 0 && g.delayed_count < g.people;

// ── Labels ──────────────────────────────────────────────────────────────────

/** `MH 803` on a flight, else the service as typed ("Coach 2"), else null. */
export function serviceLabel(row: { flight: Pick<FlightSummary, "carrier_code" | "flight_number"> | null; service_no: string | null }): string | null {
	if (row.flight) return formatFlightDesignator(row.flight.carrier_code, row.flight.flight_number);
	return row.service_no;
}
